/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { ActionPlanForm } from '@/components/ActionPlan/Form';
import { Drawer } from '@/components/views/Resolution886/components/ActionPlan/Drawer';
import { useCreateActionPlan } from '@/hooks/v2/useCreateActionPlan';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetResolution886Attachments } from '@/components/ActionPlan/UploadFile/hooks/useGetResolution886Attachments';
import { CreateActionPlanPayload, Task, FormDataValues, TaskType, PriorityType } from './types';
import { useGetActionPlan } from '@/hooks/v2/useGetActionPlan';
import { useGetActionPlanTasks } from '@/hooks/v2/useGetActionPlanTasks';
import { useUpdateActionPlan } from '@/hooks/v2/useUpdateActionPlan';
import { RiskBadge } from '@/components/views/Resolution886/components/RiskBadge';

interface ActionPlanProps {
	open?: boolean;
	title?: string;
	risk?: string;
	onClose: () => void;
}

const { useForm } = Form;

export function ActionPlan({ open = false, title = '', risk, onClose }: Readonly<ActionPlanProps>): JSX.Element {
	const [form] = useForm<FormDataValues>();

	const [actionPlanTasks, setActionPlanTasks] = useState<Task[]>([]);

	const { organization, company } = useApplicationContext();
	const { mutateAsync: createActionPlan, isLoading: isLoadingCreateActionPlan } = useCreateActionPlan();
	const { mutateAsync: updateActionPlan, isLoading: isLoadingUpdateActionPlan } = useUpdateActionPlan();
	const { report, assessment, categoryResultId, lastReviewId, actionPlan, handleSetActionPlan } =
		useResolution886Report();

	const descriptionTasks: string = I18n.get(
		'Each task corresponds to a specific administrative or engineering measure. Modify the person responsible and the due date, and keep the “Administrative” or “Engineering” prefix when modifying the task name. To create new tasks, always identify them with the corresponding measure prefix in the title.'
	);

	const {
		isLoading: isLoadingAttachments,
		data: attachments = [],
		isSuccess
	} = useGetResolution886Attachments({
		organization_id: organization?.id!,
		company_id: company?.id!,
		report_id: report?.id!,
		review_id: lastReviewId!,
		assessment_id: assessment?.id!,
		category_result_id: categoryResultId!
	});

	const { data: actionPlanData } = useGetActionPlan({
		id: actionPlan?.id!,
		organization_id: organization?.id!,
		company_id: company?.id!
	});

	const { data: actionPlanTasksData } = useGetActionPlanTasks({
		organization_id: organization?.id!,
		company_id: company?.id!,
		action_plan_id: actionPlan?.id!
	});

	useEffect(() => {
		if (actionPlanTasksData && actionPlanTasksData.data) {
			setActionPlanTasks(
				actionPlanTasksData.data.map((task) => ({
					id: task.id,
					title: task.title,
					due_date: task.due_date ? moment(task.due_date) : undefined,
					responsible_user_id: task.responsible_user_id,
					is_completed: task.is_completed,
					type: task.type as TaskType
				}))
			);
		} else {
			setActionPlanTasks([
				{
					is_completed: false,
					title: I18n.get('Administrative'),
					due_date: undefined,
					responsible_user_id: undefined,
					default: true,
					type: TaskType.ADMINISTRATIVE
				},
				{
					is_completed: false,
					title: I18n.get('Engineering'),
					due_date: undefined,
					responsible_user_id: undefined,
					default: true,
					type: TaskType.ENGINEERING
				}
			]);
		}
	}, [actionPlanTasksData]);

	useEffect(() => {
		if (attachments && isSuccess) {
			form.setFieldsValue({ attachments: attachments });
		}
	}, [attachments, isSuccess]);

	useEffect(() => {
		if (open && title) {
			form.setFieldsValue({ title });
		}
	}, [open, title]);

	useEffect(() => {
		if (actionPlanData) {
			handleSetActionPlan({
				id: actionPlanData.id,
				title: actionPlanData.title,
				due_date: actionPlanData.due_date
			});

			form.setFieldsValue({
				title: actionPlanData.title,
				responsible: actionPlanData.responsible_user_id,
				due_date: moment(actionPlanData.due_date),
				investment_range: actionPlanData.investment_range,
				investment_value: actionPlanData.investment_value || undefined,
				priority: actionPlanData.priority as PriorityType,
				description: actionPlanData.description || undefined
			});
		} else {
			form.resetFields();
			form.setFieldsValue({ title });
		}
	}, [actionPlanData]);

	function mountPayload(values: FormDataValues): CreateActionPlanPayload {
		return {
			organization_id: organization?.id!,
			company_id: company?.id!,
			workstation_id: report?.workstation?.id!,
			title: values.title,
			responsible_user_id: values.responsible,
			due_date: moment(values.due_date).format(),
			priority: values.priority,
			investment_range: values.investment_range,
			investment_value: values.investment_value,
			description: values.description,
			tasks: values.task_list?.map((task) => ({
				title: task.title,
				due_date: task.due_date ? moment(task.due_date).format() : undefined,
				responsible_user_id: task.responsible_user_id ? task.responsible_user_id : undefined,
				is_completed: task.is_completed,
				type: task.type || undefined
			})),
			attachments: values.attachments.map((attachment) => attachment.id),
			attachments_temporary: true,
			origin: {
				origin_name: 'Resolution 886',
				table_name: 'resolution_886_assessment_category_results',
				column_id: categoryResultId!,
				file_id: assessment?.file?.id!
			}
		};
	}

	function mountPayloadUpdate(values: FormDataValues): CreateActionPlanPayload {
		return {
			id: actionPlan?.id,
			organization_id: organization?.id!,
			company_id: company?.id!,
			workstation_id: report?.workstation?.id!,
			title: values.title,
			responsible_user_id: values.responsible,
			due_date: moment(values.due_date).format(),
			investment_range: values.investment_range,
			investment_value: values.investment_value,
			priority: values.priority,
			description: values.description,
		};
	}

	async function handleOnSubmit(): Promise<void> {
		const values = await form.validateFields();
		const payload = actionPlan?.id ? mountPayloadUpdate(values) : mountPayload(values);

		if (actionPlan?.id) {
			await updateActionPlan(payload);
		} else {
			const { data } = await createActionPlan(payload);
			handleSetActionPlan({
				id: data.id,
				title: data.title,
				due_date: data.due_date as Date
			});
		}
		onClose();
	}

	useEffect(() => {
		if (actionPlanTasks) {
			form.setFieldsValue({
				task_list: actionPlanTasks
			});
		}
	}, [actionPlanTasks]);

	function handleOnCancel(): void {
		form.resetFields();
	}

	if (isLoadingAttachments) {
		return <></>;
	}

	const initialValues = {
		task_list: actionPlanTasks,
		attachments: attachments
	};

	return (
		<Drawer
			edition={!!actionPlan?.id}
			open={open}
			loading={isLoadingCreateActionPlan || isLoadingUpdateActionPlan}
			onSubmit={handleOnSubmit}
			onCancel={handleOnCancel}
			onClose={onClose}
		>
			<ActionPlanForm
				form={form}
				initialValues={initialValues}
				risk={<RiskBadge risk={risk} />}
				descriptionTasks={descriptionTasks}
				origin_table="resolution_886_assessment_category_results"
				table_origin_id={categoryResultId!}
				actionPlanId={actionPlan?.id}
			/>
		</Drawer>
	);
}
